/**
 * 屏幕常亮锁(V2 批次A)。用户报障:语音对练放着一会儿手机就黑屏。
 *
 * 用法:`useWakeLock(phase 处于对练中)` —— 传意图,不传动作。进对练要锁,离开/结束还锁。
 *
 * 三条不能省的规矩:
 * 1. **意图翻转要能追平迟到的锁**:request 是异步的,await 期间用户可能已经退出;
 *    迟到的 sentinel 必须立刻 release,否则用户回到列表页手机还亮着(同 VoiceCoachingChat
 *    的 startGen 作废手法)。
 * 2. **页面 hidden 时系统会自动收走锁**(规范行为,不是 bug)—— 回前台必须重新申请,
 *    漏了就是「切一次微信回来再也不亮」。这里监听 sentinel 的 release 事件 +
 *    visibilitychange 双保险:低电量策略下页面没 hidden 也可能被收走。
 * 3. **全程静默失败**:亮屏是锦上添花,任何异常都不许挡住对练主流程。
 *
 * 不做 NoSleep.js 那种「无声循环 video」兜底:目标机型(iOS 16.4+ / 安卓 Chromium 84+,
 * 含企微 XWeb 138 与鸿蒙 ArkWeb 132)原生支持率已经很高,而 video 兜底要塞一段无法在
 * 单测里验真的 base64 视频。不支持的环境改走起始屏文案提示(见 VoiceCoachingChat)。
 */
import { useEffect, useRef, useState } from "react";

interface WakeLockSentinelLike {
  release(): Promise<void> | void;
  addEventListener?(type: string, listener: () => void): void;
}

interface WakeLockLike {
  request(type: string): Promise<WakeLockSentinelLike>;
}

function getWakeLock(): WakeLockLike | null {
  const nav = navigator as unknown as { wakeLock?: WakeLockLike };
  return nav.wakeLock ?? null;
}

export function useWakeLock(enabled: boolean): { supported: boolean; active: boolean } {
  const [active, setActive] = useState(false);
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);
  // 意图代:每次 enabled 翻转 +1;迟到的 request 结果拿旧代号 → 判废并立刻还锁
  const genRef = useRef(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const supported = getWakeLock() !== null;

  useEffect(() => {
    const wakeLock = getWakeLock();
    if (!wakeLock) return;

    const releaseNow = () => {
      const held = sentinelRef.current;
      sentinelRef.current = null;
      setActive(false);
      if (!held) return;
      try {
        void Promise.resolve(held.release()).catch(() => undefined);
      } catch {
        // 已释放/环境异常,忽略 —— 还锁失败不许冒泡
      }
    };

    const acquire = async () => {
      if (sentinelRef.current) return; // 已持有,不重复要
      const gen = ++genRef.current;
      try {
        const sentinel = await wakeLock.request("screen");
        if (genRef.current !== gen || !enabledRef.current) {
          // 等锁期间用户已经退出:迟到的锁立刻还,绝不留后台常亮
          void Promise.resolve(sentinel.release()).catch(() => undefined);
          return;
        }
        sentinelRef.current = sentinel;
        setActive(true);
        // 系统收走锁时清引用,好让 visibilitychange 那条腿能重新申请
        sentinel.addEventListener?.("release", () => {
          sentinelRef.current = null;
          setActive(false);
        });
      } catch {
        // 权限策略拦截 / 环境不支持:静默降级,不挡对练
        setActive(false);
      }
    };

    if (enabled) {
      void acquire();
    } else {
      genRef.current += 1; // 作废在途申请
      releaseNow();
    }

    // 回到前台且仍该亮 → 补申请(hidden 期间系统必然已经收走)
    const onVisibility = () => {
      if (document.visibilityState === "visible" && enabledRef.current && !sentinelRef.current) {
        void acquire();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (enabled) {
        genRef.current += 1;
        releaseNow();
      }
    };
  }, [enabled]);

  return { supported, active };
}
